import { useCallback, useRef } from 'react';
import { clamp } from 'lodash';
import {
  ASPECT_RATIOS,
  DATA_POINT_DEFAULT_VALUES,
  DATA_POINTS,
  LIM_45,
  MIN_BLOCK_VISIBLE_RATIO,
  MOTION_TYPES,
} from '../constants';
import type { AspectRatio, MotionType, TextAlignment } from '../types';

/**
 * Determines the section of a container based on given coordinates.
 * @param x - The x-coordinate
 * @param y - The y-coordinate
 * @param containerWidth - The width of the container
 * @param containerHeight - The height of the container
 * @returns A string representing the section (e.g., 'TOP_LEFT', 'MIDDLE_CENTER')
 */
const getSection = (
  value: number,
  third: number,
  sections: [string, string, string]
): string => {
  if (value < third) return sections[0]; // First section
  if (value < 2 * third) return sections[1]; // Second section
  return sections[2]; // Third section
};

// Usage in determineSection function
export const determineSection = (
  x: number,
  y: number,
  containerWidth: number,
  containerHeight: number
): string => {
  const thirdWidth = containerWidth / 3;
  const thirdHeight = containerHeight / 3;

  const horizontalSection = getSection(x, thirdWidth, [
    'LEFT',
    'CENTER',
    'RIGHT',
  ]);
  const verticalSection = getSection(y, thirdHeight, [
    'TOP',
    'MIDDLE',
    'BOTTOM',
  ]);

  return `${verticalSection}_${horizontalSection}`;
};

/**
 * Calculates a new position based on old dimensions and new dimensions.
 * @param oldX - The old x-coordinate
 * @param oldY - The old y-coordinate
 * @param oldWidth - The old width
 * @param oldHeight - The old height
 * @param newWidth - The new width
 * @param newHeight - The new height
 * @returns An object with new x and y coordinates
 */
export const calculateNewPosition = (
  oldX: number,
  oldY: number,
  oldWidth: number,
  oldHeight: number,
  newWidth: number,
  newHeight: number
): { x: number; y: number } => {
  const relativeX = oldX / oldWidth;
  const relativeY = oldY / oldHeight;

  return {
    x: relativeX * newWidth,
    y: relativeY * newHeight,
  };
};

/**
 * Calculates the dimensions of a child rectangle within a parent rectangle,
 * maintaining the child's aspect ratio.
 * @param parentWidth - The width of the parent rectangle
 * @param parentHeight - The height of the parent rectangle
 * @param childAspectRatio - The aspect ratio of the child rectangle
 * @returns An object with the calculated width and height of the child rectangle
 */
export const calculateChildRectangle = (
  parentWidth: number,
  parentHeight: number,
  childAspectRatio: AspectRatio
): { width: number; height: number } => {
  const [widthRatio, heightRatio] = childAspectRatio?.split(':')?.map(Number);
  const aspectRatio = widthRatio / heightRatio;

  let childWidth = parentWidth;
  let childHeight = parentWidth / aspectRatio;

  if (childHeight > parentHeight) {
    childHeight = parentHeight;
    childWidth = parentHeight * aspectRatio;
  }

  return { width: childWidth, height: childHeight };
};

const getTrueDimensions = (
  [bboxW, bboxH]: [number, number],
  rotation: number = 0
): [number, number] => {
  if (!rotation) return [bboxW, bboxH];
  let theta = (Math.abs(rotation) * Math.PI) / 180;
  // a rotation angle of +/-45* causes a divide by 0 problem, so set it to lim -> 45
  // note: this loses precision and have a slight offset, it is best to set the angle to not be 45 further upstream,
  // but putting this guard here since if not set, the block will get a very high number and disappear from the screen
  if (Math.abs(rotation) === 45.0) {
    theta = (Math.abs(LIM_45) * Math.PI) / 180;
  }

  const x =
    (bboxW * Math.cos(theta) - bboxH * Math.sin(theta)) /
    (Math.cos(theta) * Math.cos(theta) - Math.sin(theta) * Math.sin(theta));
  const y =
    (bboxW * Math.sin(theta) - bboxH * Math.cos(theta)) /
    (Math.sin(theta) * Math.sin(theta) - Math.cos(theta) * Math.cos(theta));
  return [x, y];
};

type VerticalSection = 'TOP' | 'MIDDLE' | 'BOTTOM';
type HorizontalSection = 'LEFT' | 'CENTER' | 'RIGHT';

/**
 * Transforms a position based on the container dimensions and section.
 * @param x - The x-coordinate, from the top left corner of block increasing right
 * @param y - The y-coordinate, from the top left corner of block increasing down
 * @param containerWidth - The width of the container
 * @param containerHeight - The height of the container
 * @param section - The section identifier
 * @param elementWidth - The width of the element being positioned
 * @param elementHeight - The height of the element being positioned
 * @param normalize - Whether to normalize the position (default: true), normalizing converts to a percentage [0,1], and de-normalizing returns position on the template in px
 * @returns An object with transformed x and y coordinates
 */
export const transformPosition = (
  x: number,
  y: number,
  containerWidth: number,
  containerHeight: number,
  section: string,
  elementWidth: number,
  elementHeight: number,
  rotation: number = 0,
  normalize: boolean = false
): { x: number; y: number } => {
  const [vertical, horizontal] = section?.split('_') as [
    VerticalSection,
    HorizontalSection,
  ];

  // element height and width are from the bounding rectangle,
  // need to calculate dimensions to account for when block is rotated
  const trueDimensions = getTrueDimensions(
    [elementWidth, elementHeight],
    rotation
  );
  const [trueWidth, trueHeight] = trueDimensions;

  const horizontalAdjustments: Record<HorizontalSection, number> = {
    LEFT: 0,
    CENTER: trueWidth / 2,
    RIGHT: trueWidth,
  };

  const verticalAdjustments = {
    TOP: 0,
    MIDDLE: trueHeight / 2,
    BOTTOM: trueHeight,
  };

  const horizontalAdjust = horizontalAdjustments[horizontal];
  const verticalAdjust = verticalAdjustments[vertical];

  // Rotated blocks are always MIDDLE_CENTER, whose anchor is the block center —
  // the same point the builder rotates around and the render service anchors
  // and pivots on. So the anchor is simply the section reference point; no
  // rotation-origin correction is needed. (`getTrueDimensions` recovers the
  // block's un-rotated size from its measured bounding box so the center anchor
  // lands correctly.)
  if (normalize) {
    return {
      x: clamp((x + horizontalAdjust) / containerWidth, 0, 1),
      y: clamp((y + verticalAdjust) / containerHeight, 0, 1),
    };
  }
  return {
    x: x * containerWidth - horizontalAdjust,
    y: y * containerHeight - verticalAdjust,
  };
};

/**
 * Computes the allowed range for a block's section anchor along one axis so the
 * block keeps at least MIN_BLOCK_VISIBLE_RATIO of the frame overlapping (a block
 * may overflow the frame but can't be moved completely out of view), while
 * never pushing the anchor outside the container — which keeps the saved,
 * normalized [0,1] anchor consistent with what's shown.
 *
 * @param anchorOffset - distance from the block's start edge to the anchor point
 *   (0 for LEFT/TOP, dim/2 for CENTER/MIDDLE, dim for RIGHT/BOTTOM)
 * @param blockDim - the block's size along this axis
 * @param containerStart - the container's start coordinate on this axis
 * @param containerDim - the container's size along this axis
 */
export const visibleAnchorBounds = (
  anchorOffset: number,
  blockDim: number,
  containerStart: number,
  containerDim: number
): { min: number; max: number } => {
  const minVisible = Math.min(blockDim, containerDim * MIN_BLOCK_VISIBLE_RATIO);
  const containerEnd = containerStart + containerDim;
  return {
    min: Math.max(
      containerStart,
      containerStart + minVisible + anchorOffset - blockDim
    ),
    max: Math.min(containerEnd, containerEnd - minVisible + anchorOffset),
  };
};

/**
 * Returns a resting position for a block that keeps a minimum visible portion
 * of the block within the frame (the same rule the drag modifier enforces), so
 * resizing or an aspect-ratio change can't leave a block stranded off-screen.
 * The result matches what a save + reload would produce (normalize ->
 * de-normalize round trip), so the builder preview stays consistent with the
 * render service.
 *
 * All dimensions are expected in the same (unscaled) coordinate space as
 * `block.position`.
 */
export const clampBlockPositionToBounds = ({
  position,
  section,
  rotation,
  containerWidth,
  containerHeight,
  elementWidth,
  elementHeight,
}: {
  position: { x: number; y: number };
  section: string;
  rotation: number;
  containerWidth: number;
  containerHeight: number;
  elementWidth: number;
  elementHeight: number;
}): { x: number; y: number } => {
  const normalized = transformPosition(
    position.x,
    position.y,
    containerWidth,
    containerHeight,
    section,
    elementWidth,
    elementHeight,
    rotation,
    true
  );

  // Keep a minimum visible margin of the block in view.
  const [vertical, horizontal] = String(section ?? '')
    .toUpperCase()
    .split('_');
  const horizontalOffsetByCol: Record<string, number> = {
    LEFT: 0,
    CENTER: elementWidth / 2,
    RIGHT: elementWidth,
  };
  const verticalOffsetByRow: Record<string, number> = {
    TOP: 0,
    MIDDLE: elementHeight / 2,
    BOTTOM: elementHeight,
  };
  const horizontalOffset =
    horizontalOffsetByCol[horizontal] ?? elementWidth / 2;
  const verticalOffset = verticalOffsetByRow[vertical] ?? elementHeight / 2;

  const xBounds = visibleAnchorBounds(
    horizontalOffset,
    elementWidth,
    0,
    containerWidth
  );
  const yBounds = visibleAnchorBounds(
    verticalOffset,
    elementHeight,
    0,
    containerHeight
  );

  const clampedX =
    clamp(normalized.x * containerWidth, xBounds.min, xBounds.max) /
    containerWidth;
  const clampedY =
    clamp(normalized.y * containerHeight, yBounds.min, yBounds.max) /
    containerHeight;

  return transformPosition(
    clampedX,
    clampedY,
    containerWidth,
    containerHeight,
    section,
    elementWidth,
    elementHeight,
    rotation,
    false
  );
};

/**
 * Normalizes a block's anchor (section + text alignment) to the rules the
 * builder, preview, and render service all agree on:
 * - Rotated blocks are center-anchored (MIDDLE_CENTER), so the builder's center
 *   rotation pivot matches the render service.
 * - Scrolling blocks are left-aligned (they span the full width and render
 *   left).
 * - Otherwise the section's horizontal component tracks the chosen alignment,
 *   while its vertical component is preserved.
 */
export const resolveBlockAnchor = ({
  motion,
  rotation,
  section,
  textAlignment,
}: {
  motion: MotionType;
  rotation: number;
  section: string;
  textAlignment: TextAlignment;
}): { section: string; textAlignment: TextAlignment } => {
  if (rotation !== 0) {
    return { section: 'MIDDLE_CENTER', textAlignment: 'CENTER' };
  }

  const [vertical] = section.split('_');

  if (motion !== MOTION_TYPES.NONE) {
    return { section: `${vertical}_LEFT`, textAlignment: 'LEFT' };
  }

  return { section: `${vertical}_${textAlignment}`, textAlignment };
};

// This is a modifier to be applied to the DndContext that restricts a rectangle to a containing rectangle
// and also accounts for the rectangle being rotated
export const useRestrictToContainingRect = () => {
  const initialBlockPosition = useRef<DOMRect | null>(null);
  const isDragging = useRef(false);

  const restrictToContainingRectHere = useCallback(
    ({ transform, draggingNodeRect, containerNodeRect, active }) => {
      if (!draggingNodeRect || !containerNodeRect) {
        isDragging.current = false;
        return transform;
      }
      if (!isDragging.current) {
        isDragging.current = true;
        initialBlockPosition.current = active.data.current.blockBoundingClient;
      }

      const value = { ...transform };

      const block = active.data.current;
      const blockRect = block.blockBoundingClient as DOMRect; // the boundingClientRect on the watermark block

      // the blockRectDimensions provided by react dnd-kit for now have the same height and width as
      // the boundingClientRect on the block Ref when there is no rotation.
      // However, when the rectangle is rotated, both the height and width of the dnd-kit element
      // are longer than the height and width of the boundingClientRect on the DOM by a factor of cos(theta)
      // the offset is also uneven, so ge the dimensions from the ref that is being passed in the data and use those for calculations
      const blockRectDimensions = {
        height: blockRect.height || draggingNodeRect.height,
        width: blockRect.width || draggingNodeRect.width,
      };

      const leftOffset =
        initialBlockPosition.current?.left - draggingNodeRect.left;
      const topOffset =
        initialBlockPosition.current?.top - draggingNodeRect.top;

      // The section reference point is the anchor the wmid render service
      // positions the block by (e.g. TOP_LEFT anchors the top-left corner,
      // MIDDLE_CENTER the center). We constrain this anchor point rather than
      // the whole box, so blocks of any size stay draggable and are free to
      // overflow the frame — matching how the block actually renders on the
      // asset, where the anchor is clamped to [0,1] but the text may spill off.
      const [vertical, horizontal] = String(block.section ?? '')
        .toUpperCase()
        .split('_');
      const verticalAnchorByRow: Record<string, number> = {
        TOP: 0,
        MIDDLE: blockRectDimensions.height / 2,
        BOTTOM: blockRectDimensions.height,
      };
      const horizontalAnchorByCol: Record<string, number> = {
        LEFT: 0,
        CENTER: blockRectDimensions.width / 2,
        RIGHT: blockRectDimensions.width,
      };
      const verticalAnchor =
        verticalAnchorByRow[vertical] ?? blockRectDimensions.height / 2;
      const horizontalAnchor =
        horizontalAnchorByCol[horizontal] ?? blockRectDimensions.width / 2;

      // Clamps the drag delta on a single axis so the block keeps a minimum
      // visible margin in the frame (see visibleAnchorBounds). The allowed range
      // is expanded to include the anchor's resting position, so a block that is
      // already off-screen (e.g. a legacy template) isn't snapped on drag start
      // — the user can still drag it back toward the frame, but not further out.
      const clampAxis = ({
        nodeStart,
        offset,
        blockDim,
        containerStart,
        containerDim,
        delta,
        anchor,
      }: {
        nodeStart: number;
        offset: number;
        blockDim: number;
        containerStart: number;
        containerDim: number;
        delta: number;
        anchor: number;
      }): number => {
        const anchorStart = nodeStart + offset + anchor;
        const { min, max } = visibleAnchorBounds(
          anchor,
          blockDim,
          containerStart,
          containerDim
        );
        const lowerBound = Math.min(min, anchorStart);
        const upperBound = Math.max(max, anchorStart);
        const clampedAnchor = Math.min(
          Math.max(anchorStart + delta, lowerBound),
          upperBound
        );
        return clampedAnchor - anchorStart;
      };

      value.y = clampAxis({
        nodeStart: draggingNodeRect.top,
        offset: topOffset,
        blockDim: blockRectDimensions.height,
        containerStart: containerNodeRect.top,
        containerDim: containerNodeRect.height,
        delta: transform.y,
        anchor: verticalAnchor,
      });

      // Scrolling blocks span the full width of the frame and only move
      // vertically, so lock horizontal movement.
      if (block.motion && block.motion !== 'none') {
        value.x = 0;
      } else {
        value.x = clampAxis({
          nodeStart: draggingNodeRect.left,
          offset: leftOffset,
          blockDim: blockRectDimensions.width,
          containerStart: containerNodeRect.left,
          containerDim: containerNodeRect.width,
          delta: transform.x,
          anchor: horizontalAnchor,
        });
      }

      return value;
    },
    []
  );

  return restrictToContainingRectHere;
};

/**
 * Returns the background image path for a given aspect ratio.
 * @param aspectRatio - The aspect ratio
 * @returns The path to the background image
 */
export function getBackgroundImageForAspectRatio(
  aspectRatio: AspectRatio
): string {
  const backgroundImages: Record<AspectRatio, string> = {
    [ASPECT_RATIOS.WIDE]: '/images/background-16-9.jpg',
    [ASPECT_RATIOS.STANDARD]: '/images/background-4-3.jpg',
    [ASPECT_RATIOS.VERTICAL]: '/images/background-9-16.jpg',
    [ASPECT_RATIOS.SQUARE]: '/images/background-1-1.jpg',
    [ASPECT_RATIOS.CINEMATIC]: '/images/background-239-1.jpg',
  };

  return backgroundImages[aspectRatio] || '/images/background-16-9.jpg';
}

// Generate display text based on block properties
export const getDisplayText = (block) => {
  const text = [];
  if (block.displayName)
    text.push(DATA_POINT_DEFAULT_VALUES[DATA_POINTS.USERNAME]);
  if (block.displayEmail)
    text.push(DATA_POINT_DEFAULT_VALUES[DATA_POINTS.EMAIL]);
  if (block.displayIP)
    text.push(DATA_POINT_DEFAULT_VALUES[DATA_POINTS.IP_ADDRESS]);
  if (block.displayDateTime)
    text.push(DATA_POINT_DEFAULT_VALUES[DATA_POINTS.TIMESTAMP]);
  if (block.displayShareCreator)
    text.push(DATA_POINT_DEFAULT_VALUES[DATA_POINTS.USER_INPUT]);
  if (block.customText) text.push(block.customText);
  return text.length > 0 ? text : ['[Empty Block]'];
};
